import { Checkbox, Group, IconButton, iconSizes, Stack, Table, Text, Tooltip } from '@common/ui'
import { IconChevronDown, IconChevronUp, IconInfoCircle } from '@common/ui/icons'
import React from 'react'
import { com } from '@eidu/web'
import { PermissionWithId } from '../input/PermissionsInput'
import SectionShell from './SectionShell'
import CellEditor from './CellEditor'
import { ColDef, ENTITY_COLS, EntityRow, buildEntitiesDisplayRows, cellKey, cellLabel, isLimitedForRow } from './utils'
import { CheckboxWithPolicyBadge, PolicyMatchesReadOnlyBlock, policyMatchesEntityCell } from './PolicyMatches'
import { PermissionPolicyDTO } from '../../domain/permission/PermissionPolicyDTO'
import EntityType = com.eidu.entity.type.EntityType
import EntityTypeId = com.eidu.entity.type.EntityTypeId
import EntitySelector = com.eidu.entity.selector.EntitySelector
import EntitySelectorPermission = com.eidu.entity.permission.EntitySelectorPermission
import Permission = com.eidu.entity.permission.Permission

type EntitiesSectionProps = {
  rows: readonly EntityRow[]
  open: boolean
  onToggleOpen: () => void
  cellPermissions: (row: EntityRow, col: ColDef) => PermissionWithId[]
  onToggleCell: (row: EntityRow, col: ColDef) => void
  onToggleColumn: (col: ColDef) => void
  hasColumnAll: (col: ColDef) => boolean
  expandedCell: string | null
  onToggleCellExpand: (key: string) => void
  onCloseExpansion: () => void
  entityTypes: readonly EntityType[]
  allowsPathForUser: boolean
  userEntityTypeId: EntityTypeId | undefined
  addPermission: (permission: Permission) => void
  replacePermission: (id: string, permission: Permission) => void
  removePermission: (id: string) => void
  selectedPolicies: ReadonlyArray<PermissionPolicyDTO>
}

const borderedCell: React.CSSProperties = { borderRight: '1px solid var(--trellis-color-outline)' }
const subtleBg = 'var(--trellis-color-surface-variant)'

const EntitiesSection = ({
  rows,
  open,
  onToggleOpen,
  cellPermissions,
  onToggleCell,
  onToggleColumn,
  hasColumnAll,
  expandedCell,
  onToggleCellExpand,
  onCloseExpansion,
  entityTypes,
  allowsPathForUser,
  userEntityTypeId,
  addPermission,
  replacePermission,
  removePermission,
  selectedPolicies,
}: EntitiesSectionProps) => {
  const ncols = 1 + ENTITY_COLS.length
  const hiddenCount = rows.length

  const columnState = (col: ColDef): 'checked' | 'indeterminate' | 'unchecked' => {
    if (hasColumnAll(col)) return 'checked'
    if (rows.length === 0) return 'unchecked'
    const filled = rows.filter((row) => cellPermissions(row, col).length > 0).length
    if (filled === 0) return 'unchecked'
    return 'indeterminate'
  }

  return (
    <SectionShell
      title="Types permissions"
      open={open}
      onToggleOpen={onToggleOpen}
      hasFooterToggle={hiddenCount > 0}
      footerLabel={`Show ${hiddenCount} more`}
      ncols={ncols}
      testId="matrix-section-entities"
    >
      <Table.Thead>
        <Table.Tr style={{ backgroundColor: subtleBg }}>
          <Table.Th style={{ minWidth: 240, ...borderedCell }}>
            <Text size="sm" fw={600}>
              Type
            </Text>
          </Table.Th>
          {ENTITY_COLS.map((col) => {
            const state = columnState(col)
            return (
              <Table.Th key={col.action.toString()} style={{ minWidth: 90, ...borderedCell }}>
                <Group gap="xxs" align="center" wrap="nowrap">
                  <Checkbox
                    size="xs"
                    checked={state === 'checked'}
                    indeterminate={state === 'indeterminate'}
                    onChange={() => onToggleColumn(col)}
                    data-testid={`matrix-column-master-${col.label.toLowerCase()}`}
                    aria-label={`Toggle all ${col.label}`}
                  />
                  <col.Icon size={iconSizes.xs} stroke={1.5} />
                  <Text size="sm" fw={600}>
                    {col.label}
                  </Text>
                  <Tooltip label="When checked, this permission applies to all current and future entity types.">
                    <IconInfoCircle size={iconSizes.xs} stroke={1.5} color="var(--trellis-color-text-secondary)" />
                  </Tooltip>
                </Group>
              </Table.Th>
            )
          })}
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {open &&
          buildEntitiesDisplayRows(rows).flatMap((display) => {
            if (display.kind === 'header') {
              return [
                <Table.Tr key={display.row.id}>
                  <Table.Td
                    colSpan={ncols}
                    style={{
                      backgroundColor: subtleBg,
                      borderBottom: '1px solid var(--trellis-color-outline)',
                      paddingBlock: 4,
                    }}
                  >
                    <Text size="xs" fw={600} tt="uppercase" lts={0.5}>
                      {display.row.label}
                    </Text>
                  </Table.Td>
                </Table.Tr>,
              ]
            }
            const row = display.row
            const colApplies = (col: ColDef) => !col.appliesToRow || col.appliesToRow(row)
            const expandedCols = ENTITY_COLS.filter(
              (col) => colApplies(col) && expandedCell === cellKey(row.id, col.action)
            )

            const items = [
              <Table.Tr key={row.id} onClick={onCloseExpansion} data-testid={`matrix-row-${row.id}`}>
                <Table.Td
                  style={{ ...borderedCell, paddingLeft: 'var(--trellis-spacing-lg)', backgroundColor: subtleBg }}
                >
                  {row.label}
                </Table.Td>
                {ENTITY_COLS.map((col) => {
                  // A form-only column renders an empty cell on non-form rows so the grid stays aligned.
                  if (!colApplies(col)) {
                    return <Table.Td key={col.action.toString()} style={borderedCell} />
                  }
                  const matching = cellPermissions(row, col)
                  const policyMatches = policyMatchesEntityCell(
                    selectedPolicies,
                    entityTypes,
                    row.entityTypeId,
                    col.action
                  )
                  const checked = matching.length > 0 || hasColumnAll(col)
                  const indeterminate =
                    matching.length > 0 &&
                    matching.some(({ permission }) => isLimitedForRow(permission, row.entityTypeId))
                  const key = cellKey(row.id, col.action)
                  const isExpanded = expandedCell === key
                  const label = cellLabel(matching)
                  return (
                    <Table.Td key={col.action.toString()} style={borderedCell}>
                      <Group gap={0} align="center" wrap="nowrap">
                        <CheckboxWithPolicyBadge matches={policyMatches}>
                          <Checkbox
                            size="xs"
                            checked={checked}
                            indeterminate={indeterminate}
                            onChange={() => onToggleCell(row, col)}
                            data-testid={`matrix-checkbox-${row.id}-${col.label.toLowerCase()}`}
                            aria-label={`${row.label} ${col.label}`}
                          />
                        </CheckboxWithPolicyBadge>
                        {(checked || isExpanded || policyMatches.length > 0) && (
                          <IconButton
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation()
                              onToggleCellExpand(key)
                            }}
                            aria-label={isExpanded ? 'Collapse cell' : 'Expand cell'}
                            data-testid={`matrix-expand-${row.id}-${col.label.toLowerCase()}`}
                          >
                            {isExpanded ? (
                              <IconChevronUp size={iconSizes.xs} stroke={1.5} />
                            ) : (
                              <IconChevronDown size={iconSizes.xs} stroke={1.5} />
                            )}
                          </IconButton>
                        )}
                        {label && (
                          <Text
                            data-testid={`matrix-label-${row.id}-${col.label.toLowerCase()}`}
                            c="dimmed"
                            ml="xxs"
                            fs="italic"
                            style={{ fontSize: '0.7rem', whiteSpace: 'nowrap' }}
                          >
                            {label}
                          </Text>
                        )}
                      </Group>
                    </Table.Td>
                  )
                })}
              </Table.Tr>,
            ]

            if (expandedCols.length > 0) {
              items.push(
                <Table.Tr key={`${row.id}-detail`}>
                  <Table.Td colSpan={ncols} style={{ backgroundColor: subtleBg, padding: 'var(--trellis-spacing-md)' }}>
                    <Stack gap="md">
                      {expandedCols.map((col) => {
                        const policyMatches = policyMatchesEntityCell(
                          selectedPolicies,
                          entityTypes,
                          row.entityTypeId,
                          col.action
                        )
                        return (
                          <Stack key={col.action.toString()} gap="xs">
                            <PolicyMatchesReadOnlyBlock matches={policyMatches} />
                            <CellEditor
                              row={row}
                              col={col}
                              permissions={cellPermissions(row, col)}
                              entityTypes={entityTypes}
                              allowsPath={col.allowsPath && allowsPathForUser}
                              userEntityTypeId={userEntityTypeId}
                              onAdd={() =>
                                addPermission(
                                  new EntitySelectorPermission(col.action, new EntitySelector.OfType(row.entityTypeId))
                                )
                              }
                              onReplace={replacePermission}
                              onDelete={removePermission}
                            />
                          </Stack>
                        )
                      })}
                    </Stack>
                  </Table.Td>
                </Table.Tr>
              )
            }

            return items
          })}
      </Table.Tbody>
    </SectionShell>
  )
}

export default EntitiesSection
